import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  RenderingEngine,
  csEnums,
  RENDERING_ENGINE_ID,
  TOOLGROUP_ID,
  TOOLGROUP_3D_ID,
  MPR_VOLUME_PREFIX,
  createToolGroup,
  create3DToolGroup,
  destroyToolGroup,
  csToolsUtilities,
  StudyData,
  SeriesData,
  volumeLoader,
  setVolumesForViewports,
  cache as csCache,
  cornerstoneTools,
} from '../cornerstone/init';
import {
  eventTarget as csEventTarget,
  metaData as csMetaData,
  imageLoader,
} from '@cornerstonejs/core';
import type { StudyGeometry } from '../data/providers';

/** Per-study geometry cache. Populated on first MPR open of a study and
 *  reused for the session. Backend also caches the underlying gzipped
 *  JSON on disk so cold-cache first fetch is ~5–10s for a large study;
 *  subsequent fetches are ~50ms server-side. */
const studyGeometryCache = new Map<number, StudyGeometry>();
import { useAuthStore } from '../stores/authStore';
import { getServerUrl } from '../api/client';
import {
  LayoutType, ViewerMode, ViewportMode, useViewerStore,
  getViewportMode, isVolumeMode, orientationForMode,
} from '../stores/viewerStore';
import { enableTouchGestures, disableTouchGestures } from '../utils/touchGestures';
import { loadAnnotations } from '../utils/annotations';
import { applySyncState } from '../cornerstone/synchronizers';
import { estimateVolumeBudget } from '../cornerstone/volumeBudget';
import { defaultWlForModality } from '../utils/wlPresets';
import { getProvider, DataProvider } from '../data/providers';
import { isPdfSeries, shouldPeekForPdf } from '../utils/pdfInstance';
import PdfViewport from './PdfViewport';
import VolumeWindowControl from './VolumeWindowControl';
import ModePicker from './ModePicker';

/**
 * Confirm whether a series should be rendered as a DICOM PDF.
 *
 * Step 1 — cheap heuristic: Modality === 'DOC' catches well-tagged data.
 * Step 2 — peek + parse: for series where modality doesn't make it
 *           obvious (empty, unknown, or only-one-instance), fetch the
 *           first DICOM file and try to extract the EncapsulatedDocument
 *           tag. Logs whatever SOPClassUID we found so any
 *           still-misdetected file can be diagnosed from the console.
 *
 * Returns true only when the PDF really exists in the file. Errors fall
 * through to image rendering.
 */
async function detectPdfBeforeLoad(
  provider: DataProvider,
  study: StudyData,
  series: SeriesData,
  acctoken: string | null
): Promise<boolean> {
  if (isPdfSeries(series)) {
    console.log(`[PDF] Series ${series.seriesId} matched by Modality='${series.modality}'`);
    return true;
  }
  if (!shouldPeekForPdf(series)) return false;
  console.log(
    `[PDF] Peeking series ${series.seriesId} (modality='${series.modality}', numImages=${series.numImages})…`
  );
  try {
    await provider.getPdfBlobUrl(study, series, acctoken || undefined);
    console.log(`[PDF] Series ${series.seriesId} confirmed as DICOM PDF.`);
    return true;
  } catch (e) {
    console.log(`[PDF] Series ${series.seriesId} is NOT a PDF: ${(e as Error).message}`);
    return false;
  }
}
import styles from './ViewportGrid.module.css';

interface Props {
  /** Multi-study: all loaded studies. The grid resolves a series id by
   *  searching across every study, so a series dragged from study B can be
   *  loaded into a viewport even when study A is "active". */
  studies: StudyData[];
  /** Which study is currently expanded in the side panel — drives the
   *  overlay/toolbar context. */
  activeStudyId: number | null;
  layout: LayoutType;
  onLoadedIdsChange?: (ids: Set<string>) => void;
  /** Which viewport (by index) receives the next series-click / keyboard action.
   *  Lifted to the parent so the sidebar and toolbar can read it too. */
  activeViewportIndex?: number;
  onActiveViewportChange?: (i: number) => void;
}

export interface ViewportGridHandle {
  /** Load a series into viewport 0 (or specified index) */
  loadSeries: (seriesId: number, viewportIndex?: number) => void;
  /** Current viewport-0 image as { seriesId, imageIndex }, or null if unavailable */
  getCurrentImage: () => { seriesId: number; imageIndex: number } | null;
  /** Load a series and jump to a specific frame in viewport 0 */
  jumpToImage: (seriesId: number, imageIndex: number) => void;
  /** Best-effort clear of any viewport currently holding a series from the
   *  given study. Used when the side panel closes a study to keep the
   *  viewports in sync with the loaded-studies set. */
  releaseStudy: (studyId: number) => void;
  /** Called when Cornerstone crashes decoding the image — usually means
   *  the file isn't actually a renderable image (PDF, SR, etc). Marks
   *  every currently-loaded series as a PDF candidate so PdfViewport
   *  takes over and the user sees the document instead of a blank
   *  viewport. */
  fallbackAllSeriesToPdf: () => void;
}

const TOOL_MAPPING: Record<string, string> = {
  Wwwc: 'WindowLevel',
  Pan: 'Pan',
  Zoom: 'Zoom',
  Magnify: 'Magnify',
  StackScroll: 'StackScroll',
  Probe: 'Probe',
  Length: 'Length',
  Angle: 'Angle',
  RectangleROI: 'RectangleROI',
  EllipticalROI: 'EllipticalROI',
  ArrowAnnotate: 'ArrowAnnotate',
  Crosshairs: 'Crosshairs',
};

/** Re-apply the user's selected tool to a freshly created tool group */
function reapplyActiveTool(toolGroupId: string) {
  const { activeTool } = useViewerStore.getState();
  const csToolName = TOOL_MAPPING[activeTool];
  if (!csToolName) return;

  const tg = cornerstoneTools.ToolGroupManager.getToolGroup(toolGroupId);
  if (!tg) return;

  // Deactivate the default WL first
  try { tg.setToolPassive('WindowLevel'); } catch {}
  try {
    tg.setToolActive(csToolName, {
      bindings: [{ mouseButton: cornerstoneTools.Enums.MouseBindings.Primary }],
    });
  } catch {}
}

/**
 * Build Cornerstone imageIds for a series via the active data provider.
 * - EDGEPACS: `wadouri:` URLs hitting /atlrestapi/v2/dicom (synchronous)
 * - DICOMweb: `wadors:` URLs hitting /studies/{}/series/{}/instances/{}/frames/1.
 *
 * DICOMweb mode needs the series' sopInstanceUIDs populated first — the
 * provider exposes ensureSeriesInstances() which the caller must await.
 * In EDGEPACS mode that's a no-op so the same call sequence is safe.
 */
async function buildImageIdsForSeries(
  study: StudyData,
  series: SeriesData,
  acctoken: string
): Promise<string[]> {
  const provider = getProvider();
  await provider.ensureSeriesInstances(study, series);
  return provider.buildImageIds(study, series, acctoken);
}

/** Clamp a focus window to [0, total) and shrink to ≤ budget. */
function clampWindow(
  win: { start: number; end: number } | undefined,
  total: number,
  budget: number
): { start: number; end: number } {
  let s = Math.max(0, Math.min(total - 1, win?.start ?? 0));
  let e = Math.max(s + 1, Math.min(total, win?.end ?? Math.min(total, budget)));
  if (e - s > budget) e = s + budget;
  return { start: s, end: e };
}

/**
 * Compute a W/L from the actual pixel histogram of the loaded image.
 *
 * Why: modalities routinely write a placeholder DICOM WC/WW when the
 * operator hasn't set one. Planmed MG scanners write WC=32768/WW=32768
 * on 16-bit data — mathematically valid, but it clips 74% of pixels to
 * one extreme, leaving MG scans looking totally white (see the
 * incident on 2026-09-01).
 *
 * Approach: sample ~200k pixels (stride to keep it cheap on 20 MP images),
 * find P0.5 and P99.5 percentiles. For MG, tighten to P1..P99 to exclude
 * the very-bright air pixels (raw ~65535 in MONOCHROME1) that would
 * dominate the top of the range and flatten the useful tissue contrast.
 *
 * Returns null if pixel data isn't accessible (RGB images, unsupported
 * formats) so the caller can fall back to a preset.
 */
function computeAutoWlFromPixels(vp: any, modality?: string):
    { lower: number; upper: number } | null {
  try {
    const imageId = vp.getCurrentImageId?.();
    if (!imageId) return null;
    const image: any = csCache.getImage(imageId);
    if (!image) return null;
    // Colour images use RGB and don't need windowing — bail.
    if (image.color) return null;
    const pixels: ArrayLike<number> | undefined = image.getPixelData?.();
    if (!pixels || pixels.length === 0) return null;

    // Coarse min/max in one pass so the histogram bins hug the actual range.
    // Stride keeps this <20 ms even on 3584x2796 MG images.
    const stride = Math.max(1, Math.floor(pixels.length / 200_000));
    let mn = Infinity, mx = -Infinity;
    let n = 0;
    for (let i = 0; i < pixels.length; i += stride) {
      const v = pixels[i];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
      n++;
    }
    if (mx <= mn) return null;

    // Modality LUT — apply rescale slope+intercept so the WL we set is in
    // the same output space Cornerstone uses for voiRange.
    const slope     = image.slope ?? 1;
    const intercept = image.intercept ?? 0;

    // 256-bin histogram covering [mn..mx].
    const numBins = 256;
    const binW = (mx - mn) / numBins;
    const bins = new Uint32Array(numBins);
    for (let i = 0; i < pixels.length; i += stride) {
      const b = Math.min(numBins - 1, Math.floor((pixels[i] - mn) / binW));
      bins[b]++;
    }

    const isMG = (modality || '').toUpperCase() === 'MG';

    // MG histograms are bimodal: a huge air peak at the top of the range
    // (up to 75% of pixels for MONOCHROME1 mammograms) plus a tissue
    // distribution in the middle. Simple P0.5..P99.5 percentiles keep the
    // upper bound inside the air peak because air dominates so heavily,
    // producing a "milky white" tissue where dense/fatty differences are
    // squashed into the top 20% of the display range.
    //
    // Explicit air exclusion: sample-limit is the effective total.
    // Anything in the top 15% of the value range (bins 218..255 for the
    // 256-bin default) is treated as air on MG and dropped from the
    // percentile computation. Then take P0.5..P99.5 of what's left ==
    // real tissue, which spans the full display range.
    let effectiveTotal = n;
    let effectiveCap = numBins - 1;   // last bin to consider
    if (isMG) {
      effectiveCap = Math.max(1, Math.floor(numBins * 0.85) - 1);
      effectiveTotal = 0;
      for (let i = 0; i <= effectiveCap; i++) effectiveTotal += bins[i];
      // If the image has no significant air peak (e.g. very small ROI or
      // already tightly cropped), fall back to the full histogram.
      if (effectiveTotal < n * 0.05) {
        effectiveCap = numBins - 1;
        effectiveTotal = n;
      }
    }

    // MG uses tighter percentiles (P2..P98) so the window concentrates on
    // the meat of the tissue distribution and produces the punchier mid-
    // range contrast radiologists get from vendor mammography consoles.
    // Combined with the sigmoid tone curve set on the viewport (see
    // applyAutoFitAndWl), this approximates the "film-like" appearance of
    // Planmed / Hologic / GE consoles without needing per-vendor LUTs.
    const pLo = isMG ? 0.02  : 0.005;
    const pHi = isMG ? 0.98  : 0.995;
    const loCount = effectiveTotal * pLo;
    const hiCount = effectiveTotal * pHi;
    let acc = 0, loBin = 0, hiBin = effectiveCap;
    let loFound = false;
    for (let i = 0; i <= effectiveCap; i++) {
      acc += bins[i];
      if (!loFound && acc >= loCount) { loBin = i; loFound = true; }
      if (acc >= hiCount) { hiBin = i; break; }
    }
    // Guard: if all bins landed in one place (uniform image), widen.
    if (hiBin <= loBin) { loBin = 0; hiBin = effectiveCap; }

    const lowerRaw = mn + loBin * binW;
    const upperRaw = mn + (hiBin + 1) * binW;
    const lower = lowerRaw * slope + intercept;
    const upper = upperRaw * slope + intercept;
    return { lower, upper };
  } catch (e) {
    console.warn('[auto-wl] compute failed', e);
    return null;
  }
}

/**
 * After a stack viewport finishes loading the first image, fit to viewport
 * and apply a W/L. Preferred path is pixel-histogram-based auto-window
 * (see computeAutoWlFromPixels). Falls back to a modality preset when
 * pixel data isn't accessible.
 *
 * For MG MONOCHROME1 specifically, force invert=true so the display
 * follows the standard MG convention (dark air, bright dense tissue).
 * Cornerstone3D respects PresentationLUTShape=INVERSE which combined
 * with MONOCHROME1 cancels the invert, producing photonegative-looking
 * output that radiologists reject. This override matches every other
 * mammography viewer's default.
 */
function applyAutoFitAndWl(vp: any, modality?: string) {
  if (!vp) return;
  try { vp.resetCamera(); } catch {}

  try {
    // ---- Window / level ----
    const auto = computeAutoWlFromPixels(vp, modality);
    if (auto && isFinite(auto.upper - auto.lower) && auto.upper > auto.lower) {
      const isMG = (modality || '').toUpperCase() === 'MG';
      // MG: apply a SIGMOID VOI LUT function on top of the tighter
      // percentile window. Sigmoid gives an S-curve rather than pure
      // linear — steeper transition through the tissue midtone, softer
      // roll-off into highlights and shadows. This is the classic "film-
      // like" appearance every mammography console defaults to and is
      // what radiologists benchmark against; linear windowing looks flat
      // and washed out to a trained MG reader even when the WL bounds
      // are correct.
      const props: any = { voiRange: { lower: auto.lower, upper: auto.upper } };
      if (isMG) props.voiLUTFunction = 'SIGMOID';
      vp.setProperties(props);
    } else {
      // Fall back to the DICOM header WL when it looks plausible, else the
      // modality preset. Same "bad-VOI" heuristic as before.
      const props = vp.getProperties?.() || {};
      const range = props.voiRange;
      const ww = range ? (range.upper - range.lower) : 0;
      const bad = !range || ww < 1 || ww > 20000
        || range.upper < -5000 || range.lower > 5000;
      if (bad) {
        const wl = defaultWlForModality(modality);
        vp.setProperties({
          voiRange: { lower: wl.wc - wl.ww / 2, upper: wl.wc + wl.ww / 2 },
        });
      }
    }

    // ---- MG-specific invert override ----
    if ((modality || '').toUpperCase() === 'MG') {
      try {
        const imageId = vp.getCurrentImageId?.();
        const image: any = imageId ? csCache.getImage(imageId) : null;
        const photom = image?.photometricInterpretation
                    ?? image?.imageFrame?.photometricInterpretation;
        if (photom === 'MONOCHROME1') {
          const cur = vp.getProperties?.().invert || false;
          if (!cur) vp.setProperties({ invert: true });
        }
      } catch (e) { console.warn('[mg-invert]', e); }
    }
  } catch { /* viewport may not support VOI — stack scout etc., skip */ }

  try { vp.render(); } catch {}
}

const ViewportGrid = forwardRef<ViewportGridHandle, Props>(function ViewportGrid(
  { studies, activeStudyId, layout, onLoadedIdsChange, activeViewportIndex = 0, onActiveViewportChange },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderingEngineRef = useRef<any>(null);
  const overlaysVisible = useViewerStore((s) => s.overlaysVisible);
  const viewerMode = useViewerStore((s) => s.viewerMode);
  const viewportModes = useViewerStore((s) => s.viewportModes);
  const viewportSlabs = useViewerStore((s) => s.viewportSlabs);
  const viewport3DPresets = useViewerStore((s) => s.viewport3DPresets);
  const volumeBuildMode = useViewerStore((s) => s.volumeBuildMode);
  const focusWindows = useViewerStore((s) => s.focusWindows);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const syncWL = useViewerStore((s) => s.syncWL);
  const syncScroll = useViewerStore((s) => s.syncScroll);
  const syncCamera = useViewerStore((s) => s.syncCamera);
  const acctoken = useAuthStore((s) => s.acctoken);

  // Per-viewport loading percentage (XHR progress of current image)
  const [vpLoadingPct, setVpLoadingPct] = useState<number[]>([]);
  /** Per-viewport "building volume" indicator. Set true when a viewport is
   *  in a volumetric mode (MPR/MIP/3D) and the volume actor hasn't been
   *  attached yet. Drives the "Building volume…" overlay so the user
   *  doesn't stare at a black viewport wondering if we've hung. */
  const [vpBuildingVolume, setVpBuildingVolume] = useState<boolean[]>([]);
  // Per-viewport slice position and WW/WC
  const [sliceInfo, setSliceInfo] = useState<{ current: number; total: number }[]>([]);
  const [vpMeta, setVpMeta] = useState<{ ww: number; wc: number; zoom: number }[]>([]);
  /** Which series id is loaded in each viewport — drives the per-viewport
   *  overlay (so vp 0 showing study A's series gets study A's patient banner
   *  even when study B is the "active" one in the side panel). Indexed
   *  parallel to the viewport grid. */
  const [vpSeriesIds, setVpSeriesIds] = useState<(number | null)[]>([]);
  /** Series whose first instance has been confirmed to be a DICOM PDF.
   *  Populated by detectPdfBeforeLoad on load paths. The render gate
   *  combines this with the modality check (isPdfSeries) so PDFs are
   *  shown whether or not the PACS bothered to tag Modality='DOC'. */
  const [pdfSeriesIds, setPdfSeriesIds] = useState<Set<number>>(new Set());

  function markPdfSeries(seriesId: number) {
    setPdfSeriesIds((prev) => {
      if (prev.has(seriesId)) return prev;
      const next = new Set(prev);
      next.add(seriesId);
      return next;
    });
  }

  /** Clear the loading-progress overlay for a given viewport. Called when
   *  the load path detects a PDF series — Cornerstone's image pipeline
   *  is skipped entirely so the per-viewport loadPct stays at its seed
   *  value forever, leaving the dim overlay visible on top of the PDF
   *  iframe. */
  function clearVpLoadingPct(vpIdx: number) {
    setVpLoadingPct((prev) => {
      if (!prev[vpIdx]) return prev;
      const n = [...prev];
      n[vpIdx] = 0;
      return n;
    });
  }

  /** Bump a viewport's loading indicator to a non-zero, non-100 value so
   *  the "Loading…" overlay renders while the initial fetch/decode is in
   *  flight. Used by loadSeriesIntoStack to give immediate feedback before
   *  the first cornerstoneimageloadprogress event fires. */
  function seedVpLoadingPct(vpIdx: number, pct = 1) {
    setVpLoadingPct((prev) => {
      const n = [...prev];
      while (n.length <= vpIdx) n.push(0);
      n[vpIdx] = pct;
      return n;
    });
  }

  function setVpSeries(vpIdx: number, seriesId: number | null) {
    setVpSeriesIds((prev) => {
      const n = [...prev];
      while (n.length <= vpIdx) n.push(null);
      n[vpIdx] = seriesId;
      return n;
    });
  }
  // Track all loaded imageIds for series-level progress
  const loadedIdsRef = useRef<Set<string>>(new Set());
  const activeIdsRef = useRef<Set<string>>(new Set());
  /** Maps every imageId in the current viewport grid back to the viewport
   *  it belongs to. Populated as each viewport's setStack resolves.
   *  Consumed by the global cornerstoneimageloadprogress handler to route
   *  per-image XHR progress to the correct viewport's loading overlay —
   *  otherwise a hanging-protocol load that puts 4 series in 4 viewports
   *  in parallel would land all progress on viewport 0 because the global
   *  handler had no other way to correlate. */
  const imageIdToVpRef = useRef<Map<string, number>>(new Map());

  const [rows, cols] = layout.split('x').map(Number);
  const totalViewports = rows * cols;

  // Derived: the active study (drives setup + overlay). Falls back to the
  // first loaded study so we don't render null while activeStudyId catches up.
  const studyData: StudyData | null =
    studies.find((s) => s.studyId === activeStudyId) ?? studies[0] ?? null;

  /** Find a series by id across ALL loaded studies + return its parent study.
   *  Lets a viewport hold a series from study B even when study A is active. */
  function findSeriesAcross(seriesId: number):
    | { series: SeriesData; study: StudyData }
    | null {
    for (const s of studies) {
      const found = s.series.find((x) => x.seriesId === seriesId);
      if (found) return { series: found, study: s };
    }
    return null;
  }

  /** Track what we last set up so re-running the setup effect on study-add
   *  events doesn't tear down all the existing viewports. We keep a
   *  hash of the per-viewport mode map so any change (single-viewport
   *  mode swap OR global preset) triggers a rebuild. */
  const lastSetupRef = useRef<{
    modesKey: string;
    totalVps: number;
    studyId: number | null;
  } | null>(null);

  /** Stable key that captures the current per-viewport mode arrangement
   *  so the setup effect can memoize on it. */
  const modesKey = (() => {
    const parts: string[] = [];
    for (let i = 0; i < totalViewports; i++) {
      parts.push(`${i}:${getViewportMode(viewportModes, i)}`);
    }
    return parts.join('|');
  })();

  /** Cheap "does the grid include any volume viewport?" check. */
  const anyVolumeMode = (() => {
    for (let i = 0; i < totalViewports; i++) {
      if (isVolumeMode(getViewportMode(viewportModes, i))) return true;
    }
    return false;
  })();

  // Which series does setupVolumeViewports build a volume from? Same heuristic
  // (largest series). Used to derive a stable key for the volume-setup effect
  // so that changing an unrelated series' focus window doesn't trigger a
  // pointless rebuild.
  const volumeSeriesId = studyData?.series
    ?.reduce<SeriesData | null>((b, s) => (!b || s.numImages > b.numImages) ? s : b, null)
    ?.seriesId ?? 0;
  const focusWindowKey = (() => {
    if (!volumeSeriesId) return '';
    const w = focusWindows[volumeSeriesId];
    return w ? `${volumeSeriesId}:${w.start}-${w.end}` : `${volumeSeriesId}:full`;
  })();

  // Maximized viewport — double-click to toggle 1x1
  const [maximizedVp, setMaximizedVp] = useState<number | null>(null);
  const prevLayoutRef = useRef<LayoutType>(layout);

  // Cornerstone progress event handlers.
  //
  // Cornerstone fires cornerstoneimageloadprogress globally without a
  // viewport hint. We correlate via imageIdToVpRef (populated by
  // setupViewports for the initial load and loadSeriesIntoStack for
  // subsequent series changes). Falls back to "first viewport still
  // loading" if the imageId isn't in the map (older code paths, or a
  // stray progress event during teardown).
  useEffect(() => {
    function findVpForImageId(imageId: string | undefined): number {
      if (imageId && imageIdToVpRef.current.has(imageId)) {
        return imageIdToVpRef.current.get(imageId)!;
      }
      // Fallback: return -1 so the caller can decide (usually first-still-loading)
      return -1;
    }

    // XHR progress — fires repeatedly as bytes download
    const onProgress = (evt: any) => {
      const pct = evt?.detail?.percentComplete;
      if (typeof pct !== 'number') return;
      const imageId: string = evt?.detail?.imageId
        ?? evt?.detail?.url
        ?? evt?.detail?.request?.responseURL
        ?? '';
      const vp = findVpForImageId(imageId);
      setVpLoadingPct((prev) => {
        const n = [...prev];
        if (vp >= 0) {
          while (n.length <= vp) n.push(0);
          if (n[vp] < 100) n[vp] = Math.round(pct);
        } else {
          // Fallback: first still-loading viewport
          for (let i = 0; i < n.length; i++) {
            if (n[i] > 0 && n[i] < 100) { n[i] = Math.round(pct); break; }
          }
        }
        return n;
      });
    };

    // Image fully loaded and decoded
    const onImageLoaded = (evt: any) => {
      const imageId = evt?.detail?.image?.imageId ?? evt?.detail?.imageId ?? '';
      if (!imageId) return;

      // Track for series-level progress
      if (activeIdsRef.current.has(imageId) && !loadedIdsRef.current.has(imageId)) {
        loadedIdsRef.current.add(imageId);
        onLoadedIdsChange?.(new Set(loadedIdsRef.current));
      }

      // Mark the CORRECT viewport as 100% loaded (not just the first one).
      const vp = findVpForImageId(imageId);
      setVpLoadingPct((prev) => {
        const n = [...prev];
        if (vp >= 0) {
          while (n.length <= vp) n.push(0);
          n[vp] = 100;
        } else {
          for (let i = 0; i < n.length; i++) {
            if (n[i] > 0 && n[i] < 100) { n[i] = 100; break; }
          }
        }
        return n;
      });
    };

    // XHR started — reset progress for the loading viewport
    const onLoadStart = (_evt: any) => {
      // No-op currently; loadSeriesIntoStack already seeds pct=1 before
      // firing setStack. Kept in case we want per-image reset later.
    };

    csEventTarget.addEventListener('cornerstoneimageloadprogress' as any, onProgress);
    csEventTarget.addEventListener('cornerstoneimageloadstart' as any, onLoadStart);
    csEventTarget.addEventListener('CORNERSTONE_IMAGE_LOADED' as any, onImageLoaded);

    return () => {
      csEventTarget.removeEventListener('cornerstoneimageloadprogress' as any, onProgress);
      csEventTarget.removeEventListener('cornerstoneimageloadstart' as any, onLoadStart);
      csEventTarget.removeEventListener('CORNERSTONE_IMAGE_LOADED' as any, onImageLoaded);
    };
  }, [onLoadedIdsChange]);

  // Listen for slice changes on viewport elements
  useEffect(() => {
    function updateVpInfo(el: HTMLElement) {
      const vpIndex = parseInt(el.id.replace('viewport-', ''), 10);
      if (isNaN(vpIndex)) return;

      const engine = renderingEngineRef.current;
      if (!engine) return;
      const vp = engine.getViewport(el.id) as any;
      if (!vp) return;

      // Skip volume viewports — they don't have stack image indices
      if (vp.type !== 'stack') return;

      let currentIndex = 0;
      let imageIds: any[] = [];
      try {
        currentIndex = vp.getCurrentImageIdIndex?.() ?? 0;
        imageIds = vp.getImageIds?.() ?? [];
      } catch { return; }

      setSliceInfo((prev) => {
        const n = [...prev];
        n[vpIndex] = { current: currentIndex, total: imageIds.length };
        return n;
      });

      // Get WW/WC and zoom
      try {
        const props = vp.getProperties?.() ?? {};
        const voiRange = props.voiRange;
        const ww = voiRange ? Math.round(voiRange.upper - voiRange.lower) : 0;
        const wc = voiRange ? Math.round((voiRange.upper + voiRange.lower) / 2) : 0;
        const zoom = Math.round((vp.getZoom?.() ?? 1) * 100) / 100;

        setVpMeta((prev) => {
          const n = [...prev];
          n[vpIndex] = { ww, wc, zoom };
          return n;
        });
      } catch { /* ignore */ }
    }

    function onStackNewImage(evt: any) {
      const el = evt.target || evt.currentTarget;
      if (el?.id) updateVpInfo(el);
    }

    function onVoiModified(evt: any) {
      const el = evt.target || evt.currentTarget;
      if (el?.id) updateVpInfo(el);
    }

    // CAMERA_MODIFIED fires on zoom/pan/rotate. Without this, the zoom-factor
    // overlay would stay frozen at the boot value (often 1×) for the whole
    // session — making the readout misleading rather than informative.
    function onCameraModified(evt: any) {
      const el = evt.target || evt.currentTarget;
      if (el?.id) updateVpInfo(el);
    }

    const elements: HTMLElement[] = [];
    for (let i = 0; i < totalViewports; i++) {
      const el = document.getElementById(`viewport-${i}`);
      if (el) {
        el.addEventListener('CORNERSTONE_STACK_NEW_IMAGE', onStackNewImage);
        el.addEventListener('CORNERSTONE_VOI_MODIFIED', onVoiModified);
        el.addEventListener('CORNERSTONE_CAMERA_MODIFIED', onCameraModified);
        elements.push(el);
      }
    }

    return () => {
      elements.forEach((el) => {
        el.removeEventListener('CORNERSTONE_STACK_NEW_IMAGE', onStackNewImage);
        el.removeEventListener('CORNERSTONE_VOI_MODIFIED', onVoiModified);
        el.removeEventListener('CORNERSTONE_CAMERA_MODIFIED', onCameraModified);
      });
    };
  }, [totalViewports, studyData]);

  // Create rendering engine once
  useEffect(() => {
    if (!containerRef.current) return;

    const renderingEngine = new RenderingEngine(RENDERING_ENGINE_ID);
    renderingEngineRef.current = renderingEngine;

    return () => {
      // Cornerstone3D + vtk.js shutdown has known ordering bugs where an
      // ImageMapper's release-graphics call runs against an already-null
      // OpenGL renderer, throwing "Cannot read properties of null (reading
      // 'releaseGraphicsResources')". The rendering context is dead at that
      // point anyway — swallow to keep the console clean on viewer close.
      try { destroyToolGroup(TOOLGROUP_ID); } catch (err) {
        console.debug('[ViewportGrid] destroyToolGroup(TOOLGROUP_ID) — harmless during teardown:', err);
      }
      try { destroyToolGroup(TOOLGROUP_3D_ID); } catch (err) {
        console.debug('[ViewportGrid] destroyToolGroup(TOOLGROUP_3D_ID) — harmless during teardown:', err);
      }
      try { renderingEngine.destroy(); } catch (err) {
        console.debug('[ViewportGrid] renderingEngine.destroy — harmless during teardown:', err);
      }
      renderingEngineRef.current = null;
    };
  }, []);

  // Attach touch gestures and load saved annotations
  useEffect(() => {
    const elements: HTMLElement[] = [];
    for (let i = 0; i < totalViewports; i++) {
      const el = document.getElementById(`viewport-${i}`);
      if (el) {
        enableTouchGestures(el);
        elements.push(el);
      }
    }

    return () => {
      elements.forEach(disableTouchGestures);
    };
  }, [totalViewports, studyData?.studyId]);

  // Set up viewports when study/layout/mode changes.
  //
  // With per-viewport modes, a single unified setup runs on any change to
  // the mode map. We short-circuit only if EVERYTHING relevant (mode map,
  // total viewport count, and — for volume-bearing grids — the active
  // study) matches what we last set up.
  useEffect(() => {
    console.log(
      `[ViewportGrid] setup-effect run studies=${studies.length} acctoken=${!!acctoken} engine=${!!renderingEngineRef.current} modesKey=${modesKey} totalVps=${totalViewports} lastSetup=${JSON.stringify(lastSetupRef.current)}`
    );
    if (!renderingEngineRef.current || studies.length === 0 || !acctoken) {
      console.log('[ViewportGrid] setup-effect bailed on guard');
      return;
    }
    const renderingEngine = renderingEngineRef.current;
    const last = lastSetupRef.current;
    const vstudy = anyVolumeMode ? (studyData ?? studies[0]) : studies[0];

    // Skip if nothing relevant has changed. All-stack grids never re-setup
    // on active-study change (would tear down user state); volume grids do.
    const studyKey = anyVolumeMode ? vstudy.studyId : studies[0].studyId;
    if (
      last &&
      last.modesKey === modesKey &&
      last.totalVps === totalViewports &&
      last.studyId === studyKey
    ) {
      console.log('[ViewportGrid] setup-effect skipping — nothing relevant changed.');
      return;
    }

    destroyToolGroup(TOOLGROUP_ID);
    destroyToolGroup(TOOLGROUP_3D_ID);
    loadedIdsRef.current.clear();
    activeIdsRef.current.clear();
    imageIdToVpRef.current.clear();
    setVpSeriesIds([]);

    setupViewports(renderingEngine, vstudy);
    lastSetupRef.current = {
      modesKey, totalVps: totalViewports, studyId: studyKey,
    };
  }, [studies, activeStudyId, totalViewports, acctoken, modesKey, anyVolumeMode, volumeBuildMode, focusWindowKey]);

  // Apply per-viewport slab thickness changes. Only affects viewports whose
  // mode uses a slab (MIP-*). MPR viewports must NOT get slab applied
  // (leaving them at 0 = thin slice, i.e. proper single-plane MPR).
  useEffect(() => {
    const engine = renderingEngineRef.current;
    if (!engine) return;
    for (let i = 0; i < totalViewports; i++) {
      const mode = getViewportMode(viewportModes, i);
      if (mode !== 'mip-ax' && mode !== 'mip-sag' && mode !== 'mip-cor') continue;
      const vp = engine.getViewport(`viewport-${i}`) as any;
      if (!vp?.setSlabThickness) continue;
      const slabMm = viewportSlabs[i] ?? 50;
      try {
        vp.setSlabThickness(slabMm);
        vp.render();
      } catch { /* viewport may not be volume-typed yet */ }
    }
  }, [viewportSlabs, viewportModes, totalViewports]);

  // Apply viewport sync whenever flags, layout, study, or mode change.
  // Defer briefly so we see viewports AFTER setStack / setVolume has resolved
  // — otherwise the cohort check would find empty viewports and camera sync
  // would be silently disabled.
  useEffect(() => {
    if (!renderingEngineRef.current) return;
    const t = setTimeout(() => applySync(), 600);
    return () => clearTimeout(t);

    function applySync() {
    if (!renderingEngineRef.current) return;
    const engine = renderingEngineRef.current;
    const viewportIds: string[] = [];
    // Group viewports by "coordinate cohort" so camera sync only runs between
    // viewports that actually share a frame. Keys:
    //   ortho volume → volumeId
    //   stack        → first seriesId parsed from imageIds
    const cohorts = new Map<string, string[]>();
    for (let i = 0; i < totalViewports; i++) {
      const vp: any = engine.getViewport(`viewport-${i}`);
      if (!vp) continue;
      const id = `viewport-${i}`;
      viewportIds.push(id);

      let cohortKey = '';
      try {
        // 3D volume viewports broadcast a freely-rotating camera that has
        // no useful meaning for orthogonal MPR slices — including them in
        // the camera sync cohort would slam the 2D viewports off-image
        // every time the user spins the 3D. So skip them entirely.
        const isVolume3D = vp.type === 'volume3d'
          || vp.constructor?.name === 'VolumeViewport3D';
        if (isVolume3D) {
          // include in `viewportIds` for WL/scroll sync but skip cohort
        } else {
          const actors = vp.getActors?.();
          if (actors && actors.length > 0 && actors[0]?.uid) {
            cohortKey = `vol:${actors[0].uid}`;
          } else {
            const imgs = vp.getImageIds?.();
            const m = imgs?.[0] ? /seriesId=(\d+)/.exec(imgs[0]) : null;
            if (m) cohortKey = `stack:${m[1]}`;
          }
        }
      } catch { /* fall through — viewport not ready */ }

      if (cohortKey) {
        const arr = cohorts.get(cohortKey) ?? [];
        arr.push(id);
        cohorts.set(cohortKey, arr);
      }
    }

    // Camera sync eligible = the largest cohort (there's usually only one in
    // MPR mode; in stack mode with different series, the "largest" of the
    // same-series group still gets camera sync, the rest are excluded).
    let cameraEligible: string[] = [];
    for (const arr of cohorts.values()) {
      if (arr.length > cameraEligible.length) cameraEligible = arr;
    }

    if (viewportIds.length < 2) {
      applySyncState({ viewportIds, syncWL: false, syncScroll: false, syncCamera: false });
      return;
    }
    applySyncState({
      viewportIds,
      cameraViewportIds: cameraEligible,
      syncWL,
      syncScroll,
      syncCamera,
    });
    }
  }, [syncWL, syncScroll, syncCamera, totalViewports, studyData?.studyId, viewerMode]);

  /**
   * Unified per-viewport setup. Reads viewportModes from the store and
   * builds a Cornerstone viewport of the right type for each pane:
   *   'stack'                     → STACK viewport
   *   'mpr-{ax,sag,cor}'          → ORTHOGRAPHIC viewport, orientation set
   *   'mip-{ax,sag,cor}'          → ORTHOGRAPHIC + MIP blend + slab
   *   '3d-vr'                     → VOLUME_3D viewport, VR preset applied
   *
   * Volume/3D viewports share ONE volume actor (built from the study's
   * largest series). Stack viewports are auto-loaded only for viewport 0
   * (matches historical behaviour — others fill via drag-drop).
   *
   * Historical rendering bugs fixed here (OHIF-verified pattern):
   *   1. `await volume.load()` before setVolumesForViewports so dims are
   *      correct — previous "load-and-forget" caused partial-metadata
   *      volumes to render at wrong dimensions.
   *   2. `setProperties({...}, volumeId)` passes the volume actor UID so
   *      the VOI actually applies to the volume (was applying to an
   *      unspecified default actor and silently no-op'ing).
   *   3. `sampleDistanceMultiplier` removed — not a real CS3D property.
   *   4. NO `resetCamera()` on ortho viewports before setProperties.
   *      `setVolumesForViewports` places the camera correctly; a stray
   *      `resetCamera()` can leave the ortho showing region OUTSIDE the
   *      volume, which vtk.js samples with texture wrap → doubled/tiled
   *      sagittal + coronal.
   *   5. NO `setSlabThickness` for MPR mode. A slab thinner than voxel
   *      spacing makes vtk.js sample UVs outside [0,1] with REPEAT wrap
   *      on some GPU drivers → same doubling. Only MIP mode sets a slab.
   *   6. MPR viewports explicitly set `BlendModes.COMPOSITE` (default,
   *      but defensively re-asserted so any leftover MIP state is undone).
   */
  async function setupViewports(renderingEngine: any, study: StudyData) {
    const modes: ViewportMode[] = [];
    for (let i = 0; i < totalViewports; i++) {
      modes.push(getViewportMode(viewportModes, i));
    }

    console.log(
      `[ViewportGrid] setupViewports study=${study.studyId} totalVps=${totalViewports}`,
      { modes }
    );

    setVolumeError(null);

    const stackVpIndices: number[] = [];
    const orthoVpIndices: number[] = [];  // MPR + MIP
    const threeDVpIndices: number[] = [];
    const vpInputs: any[] = [];
    const pctInit: number[] = [];

    for (let i = 0; i < totalViewports; i++) {
      const element = document.getElementById(`viewport-${i}`);
      if (!element) continue;
      const mode = modes[i];

      if (mode === 'stack') {
        vpInputs.push({
          viewportId: `viewport-${i}`,
          type: csEnums.ViewportType.STACK,
          element,
          defaultOptions: { background: [0, 0, 0] as [number, number, number] },
        });
        stackVpIndices.push(i);
        pctInit.push(i === 0 ? 1 : 0);
      } else if (mode === '3d-vr') {
        vpInputs.push({
          viewportId: `viewport-${i}`,
          type: csEnums.ViewportType.VOLUME_3D,
          element,
          defaultOptions: { background: [0, 0, 0] as [number, number, number] },
        });
        threeDVpIndices.push(i);
        pctInit.push(0);
      } else {
        // MPR / MIP orthographic
        const axis = orientationForMode(mode);
        let orientation: any;
        switch (axis) {
          case 'sagittal': orientation = csEnums.OrientationAxis.SAGITTAL; break;
          case 'coronal':  orientation = csEnums.OrientationAxis.CORONAL; break;
          default:         orientation = csEnums.OrientationAxis.AXIAL;
        }
        vpInputs.push({
          viewportId: `viewport-${i}`,
          type: csEnums.ViewportType.ORTHOGRAPHIC,
          element,
          defaultOptions: {
            orientation,
            background: [0, 0, 0] as [number, number, number],
          },
        });
        orthoVpIndices.push(i);
        pctInit.push(0);
      }
    }

    if (vpInputs.length === 0) return;
    setVpLoadingPct(pctInit);
    renderingEngine.setViewports(vpInputs);

    // Tool groups. Main toolgroup gets stack + ortho viewports; 3D
    // toolgroup gets VOLUME_3D viewports (they take a different set of
    // interactive tools — trackball rotate).
    const toolGroup = createToolGroup(TOOLGROUP_ID);
    for (const idx of stackVpIndices) {
      toolGroup?.addViewport(`viewport-${idx}`, RENDERING_ENGINE_ID);
    }
    for (const idx of orthoVpIndices) {
      toolGroup?.addViewport(`viewport-${idx}`, RENDERING_ENGINE_ID);
    }

    if (threeDVpIndices.length > 0) {
      const tg3d = create3DToolGroup();
      for (const idx of threeDVpIndices) {
        tg3d?.addViewport(`viewport-${idx}`, RENDERING_ENGINE_ID);
      }
    }

    // Crosshairs/ReferenceLines only make sense across 2+ ortho panes
    // sharing a volume. Wire them onto the main toolgroup with the first
    // ortho as the reference source — setToolConfiguration updates the
    // source on subsequent runs (the tool itself is already added in
    // createToolGroup).
    if (orthoVpIndices.length >= 2) {
      const referenceSource = `viewport-${orthoVpIndices[0]}`;
      try {
        (toolGroup as any)?.setToolConfiguration(
          cornerstoneTools.ReferenceLinesTool.toolName,
          { sourceViewportId: referenceSource }
        );
        toolGroup?.setToolEnabled(cornerstoneTools.ReferenceLinesTool.toolName);
      } catch { /* older CS3D signature — fall back to re-adding */
        try {
          toolGroup?.addTool(cornerstoneTools.ReferenceLinesTool.toolName, {
            sourceViewportId: referenceSource,
          });
          toolGroup?.setToolEnabled(cornerstoneTools.ReferenceLinesTool.toolName);
        } catch {}
      }
    }

    // ----- Volume path -----
    const anyVolume = orthoVpIndices.length + threeDVpIndices.length > 0;
    let volumeId = '';
    let volumeModality = study.modality;

    if (anyVolume) {
      // Show the "Building volume…" overlay on every volume viewport
      // before we start the potentially-slow geometry fetch + volume
      // construction. Cleared per-viewport when its setVolumes resolves.
      const _volVps = [...orthoVpIndices, ...threeDVpIndices];
      setVpBuildingVolume(prev => {
        const next = prev.length === totalViewports ? [...prev] : new Array(totalViewports).fill(false);
        for (const idx of _volVps) next[idx] = true;
        return next;
      });
      const series = study.series.reduce<SeriesData | null>(
        (best, s) => (!best || s.numImages > best.numImages) ? s : best,
        null
      );
      if (!series || series.numImages < 2) {
        setVolumeError('Selected study has no series suitable for volume rendering.');
        // No volume will build — clear the overlay so viewports don't
        // sit forever showing "Building volume…".
        setVpBuildingVolume(prev => prev.map(() => false));
      } else {
        volumeModality = series.modality || study.modality;
        const budget = estimateVolumeBudget();
        const total = series.numImages;

        // Fetch (once per session) authoritative per-instance geometry
        // from the backend. Instances arrive already sorted by projected
        // Z along the scan axis normal — no client-side prefetch or
        // sort needed. Backend serves cached gzipped JSON in ~50ms after
        // the first cold generation (~5–10s for a big study).
        const provider = getProvider();
        let geometry = studyGeometryCache.get(study.studyId);
        if (!geometry && provider.getStudyGeometry) {
          const t0 = performance.now();
          try {
            geometry = (await provider.getStudyGeometry(study, acctoken!)) ?? undefined;
            if (geometry) {
              studyGeometryCache.set(study.studyId, geometry);
              console.info(
                `[volume] backend geometry fetched in ${Math.round(performance.now() - t0)}ms`
              );
            }
          } catch (err) {
            console.warn('[volume] getStudyGeometry failed:', err);
          }
        }

        // Look up the target series in the geometry payload.
        const seriesGeom = geometry?.series?.find(s => s.seriesId === series.seriesId);
        const instances = seriesGeom?.instances ?? [];

        // Build the full base URL array (indexed by original imageIndex).
        // buildImageIdsForSeries already produces one entry per DICOM
        // in image_number_n order — so `allIds[imageIndex]` is the
        // correct URL for imageIndex.
        const allIds = await buildImageIdsForSeries(study, series, acctoken!);

        // Rewrite allIds in the physically-sorted order given by the
        // backend geometry. Fall back to unsorted if geometry missing
        // (DICOMweb backend, or backend endpoint not deployed yet).
        let sortedIds: string[];
        if (instances.length > 0) {
          sortedIds = instances
            .map(inst => allIds[inst.imageIndex])
            .filter(Boolean);
          if (sortedIds.length !== instances.length) {
            console.warn(
              `[volume] ${instances.length - sortedIds.length} instance imageIndex entries didn't map to a URL — falling back to unsorted`
            );
            sortedIds = allIds;
          } else {
            console.info(
              `[volume] using backend-sorted imageIds (${sortedIds.length} slices)`
            );
          }
        } else {
          if (provider.getStudyGeometry) {
            console.warn(
              '[volume] backend returned no instances for this series — volume Z spacing may be wrong'
            );
          }
          sortedIds = allIds;
        }

        const { volumeBuildMode: bMode, focusWindows: fw } = useViewerStore.getState();

        // Now slice a window. Sorted array = physical Z order, so
        // `slice(start, end)` gives us a PHYSICALLY CONTIGUOUS chunk
        // (uniform slice spacing). This is what CS3D's spacing calc
        // needs and lets us drop the client-side spacing override.
        let imageIds: string[];
        let volumeTag: string;
        if (bMode === 'focus' && total > budget.maxSlices) {
          const win = clampWindow(fw[series.seriesId], total, budget.maxSlices);
          imageIds = sortedIds.slice(win.start, win.end);
          volumeTag = `f${win.start}-${win.end}`;
          console.info(`[volume] Focus window ${win.start}-${win.end} of ${sortedIds.length}`);
        } else if (sortedIds.length > budget.maxSlices) {
          const startIdx = Math.floor((sortedIds.length - budget.maxSlices) / 2);
          const endIdx = startIdx + budget.maxSlices;
          imageIds = sortedIds.slice(startIdx, endIdx);
          volumeTag = `c${startIdx}-${endIdx}`;
          console.info(`[volume] Overview middle window ${startIdx}-${endIdx} of ${sortedIds.length}`);
        } else {
          imageIds = sortedIds;
          volumeTag = 'full';
          console.info(`[volume] Full series: ${sortedIds.length} slices`);
        }

        volumeId = `${MPR_VOLUME_PREFIX}${volumeTag}:${series.seriesInstanceUID || series.seriesId}`;
        try { csCache.removeVolumeLoadObject(volumeId); } catch {}

        // OHIF v3.10 verbatim pattern:
        //   1. createAndCacheVolume (awaited)
        //   2. volume.load() — FIRE AND FORGET, do not await
        //   3. Per viewport:
        //        a. await viewport.setVolumes([{ volumeId, blendMode, slabThickness }])
        //        b. viewport.render() — immediate, lets vtk.js establish
        //           default shader pipeline with CS3D's automatic TF
        //        c. setTimeout(() => {
        //             viewport.setProperties({ voiRange, preset }, volumeId);
        //             viewport.render();
        //           }, 0)
        //   The setTimeout(0) is critical — OHIF's own comment: "we need
        //   the actor to be ready first before we set the properties."
        //   Applying properties during first render causes vtk.js to
        //   recompile the shader mid-render, which on some drivers is
        //   the origin of the vtkVolumeVS.glsl x3 compile failure.
        //
        //   Source: OHIF/Viewers v3.10.0 CornerstoneViewportService.ts
        //   setVolumesForViewport().
        let volume: any = null;
        try {
          volume = await volumeLoader.createAndCacheVolume(volumeId, { imageIds });
        } catch (err) {
          console.error('[volume] createAndCacheVolume failed:', err);
          setVolumeError('Unable to build volume. Try switching back to Stack mode.');
          // Volume creation failed — clear the building overlay for all
          // volume viewports so the user isn't stuck staring at it.
          setVpBuildingVolume(prev => prev.map(() => false));
        }

        // NOTE: the previous per-texture `setOglNorm16Ext(null)` fix
        // was removed — it can't work in isolation because
        // vtk.js VolumeMapper.js:860 unconditionally re-reads
        // `gl.getExtension('EXT_texture_norm16')` on every render and
        // sets it back on the texture. The reliable fix is to hide the
        // extension at the WebGL2 context level, done in index.html
        // before any vtk.js code runs.

        if (volume) {
          // NOTE: client-side spacing override REMOVED. With backend-served
          // `study.geometry` giving us physically-sorted imageIds, CS3D 5's
          // wadouri spacing calc (which reads imageIds[0] and imageIds[middle]
          // AS-IS) now sees a monotonically Z-ordered input and produces
          // the correct spacing directly. Nothing to fix up.

          // (Empty catch preserved to keep the flow readable; drops through.)
          try {
            /* backend geometry drives spacing now; nothing to override */
          } catch (err) {
            console.warn('[volume] zSpacing override failed:', err);
          }

          // One-shot geometry log — helps diagnose aspect / spacing bugs.
          try {
            const dims = volume.dimensions ?? [0, 0, 0];
            const sp = volume.spacing ?? [0, 0, 0];
            const middleIdx = Math.floor(imageIds.length / 2);
            const first = csMetaData.get('imagePlaneModule', imageIds[0]);
            const middle = csMetaData.get('imagePlaneModule', imageIds[middleIdx]);
            const last  = csMetaData.get('imagePlaneModule', imageIds[imageIds.length - 1]);
            console.info(
              `[volume] loaded ${imageIds.length} slices; dims=[${dims.join(',')}]; ` +
              `spacing=[${sp.map((v: number) => v?.toFixed?.(3)).join(',')}]; ` +
              `physical extent (mm) = ${(dims[0]*sp[0]).toFixed(1)} × ${(dims[1]*sp[1]).toFixed(1)} × ${(dims[2]*sp[2]).toFixed(1)}`
            );
            console.info('[volume:ipp] first  (imageId slot 0)         =', first?.imagePositionPatient);
            console.info(`[volume:ipp] middle (imageId slot ${middleIdx})        =`, middle?.imagePositionPatient);
            console.info(`[volume:ipp] last   (imageId slot ${imageIds.length-1})      =`, last?.imagePositionPatient);
            console.info('[volume:iop] orientation                     =', first?.imageOrientationPatient);
            console.info('[volume:tags] first.sliceThickness=', first?.sliceThickness, 'first.spacingBetweenSlices=', first?.spacingBetweenSlices);
            console.info('[volume:tags] middle.sliceThickness=', middle?.sliceThickness, 'middle.spacingBetweenSlices=', middle?.spacingBetweenSlices);
            // Compute what CS3D's wadouri calc SHOULD produce
            if (first?.imagePositionPatient && middle?.imagePositionPatient && first?.imageOrientationPatient) {
              const iop = first.imageOrientationPatient;
              // scanAxisNormal = cross(rowCosine, colCosine)
              const nx = iop[1]*iop[5] - iop[2]*iop[4];
              const ny = iop[2]*iop[3] - iop[0]*iop[5];
              const nz = iop[0]*iop[4] - iop[1]*iop[3];
              const dx = first.imagePositionPatient[0] - middle.imagePositionPatient[0];
              const dy = first.imagePositionPatient[1] - middle.imagePositionPatient[1];
              const dz = first.imagePositionPatient[2] - middle.imagePositionPatient[2];
              const dist = dx*nx + dy*ny + dz*nz;
              const halfCount = Math.floor(imageIds.length / 2);
              console.info(
                `[volume:calc] first→middle projected distance = ${dist.toFixed(2)} mm; ` +
                `÷ (n/2 = ${halfCount}) = predicted spacing ${(Math.abs(dist)/halfCount).toFixed(3)} mm; ` +
                `CS3D actually used ${sp[2]?.toFixed?.(3)} mm`
              );
            }
          } catch (e) {
            console.warn('[volume:diag] failed:', e);
          }

          // OHIF: load() without await — voxels stream in asynchronously
          // and CS3D re-drives frames as they arrive.
          if (typeof volume.load === 'function' && !volume.loadStatus?.loading) {
            volume.load();
          }

          const wl = defaultWlForModality(volumeModality);
          const voiRange = { lower: wl.wc - wl.ww / 2, upper: wl.wc + wl.ww / 2 };

          // Per-viewport ortho (MPR / MIP)
          for (const idx of orthoVpIndices) {
            const vp = renderingEngine.getViewport(`viewport-${idx}`) as any;
            if (!vp?.setVolumes) continue;

            const mode = modes[idx];
            const isMip = mode === 'mip-ax' || mode === 'mip-sag' || mode === 'mip-cor';
            const slabMm = isMip
              ? (useViewerStore.getState().viewportSlabs[idx] ?? 50)
              : undefined;

            try {
              await vp.setVolumes([{
                volumeId,
                blendMode: isMip
                  ? csEnums.BlendModes.MAXIMUM_INTENSITY_BLEND
                  : csEnums.BlendModes.COMPOSITE,
                slabThickness: slabMm,
              }]);
              vp.render();
            } catch (err) {
              console.error(`[volume] setVolumes failed on viewport-${idx}:`, err);
              setVpBuildingVolume(prev => { const n = [...prev]; n[idx] = false; return n; });
              continue;
            }
            // First frame is drawn — clear the "Building volume…" overlay.
            setVpBuildingVolume(prev => { const n = [...prev]; n[idx] = false; return n; });

            if (idx === orthoVpIndices[0]) {
              try {
                console.info('[volume] norm16HideActive=', (window as any).__edgewebNorm16Hidden === true);
              } catch {}
            }

            // Defer VOI to next tick — OHIF's pattern.
            setTimeout(() => {
              const engine = renderingEngineRef.current;
              const vpNow: any = engine?.getViewport(`viewport-${idx}`);
              if (!vpNow) return;
              try {
                vpNow.setProperties({
                  voiRange,
                  interpolationType: csEnums.InterpolationType.LINEAR,
                }, volumeId);
              } catch {}
              try { vpNow.render(); } catch {}
            }, 0);
          }

          // Per-viewport 3D-VR
          for (const idx of threeDVpIndices) {
            const vp = renderingEngine.getViewport(`viewport-${idx}`) as any;
            if (!vp?.setVolumes) continue;
            const preset = useViewerStore.getState().viewport3DPresets[idx] ?? 'CT-Bone';
            try {
              await vp.setVolumes([{ volumeId }]);
              vp.render();
            } catch (err) {
              console.error(`[volume] setVolumes failed on 3D viewport-${idx}:`, err);
              setVpBuildingVolume(prev => { const n = [...prev]; n[idx] = false; return n; });
              continue;
            }
            setVpBuildingVolume(prev => { const n = [...prev]; n[idx] = false; return n; });
            setTimeout(() => {
              const engine = renderingEngineRef.current;
              const vpNow: any = engine?.getViewport(`viewport-${idx}`);
              if (!vpNow) return;
              try { vpNow.setProperties({ preset }, volumeId); } catch {}
              try { vpNow.render(); } catch {}
            }, 0);
          }
        }
      }
    }

    // ----- Stack path: auto-load first series into viewport 0 if it's a
    // stack (matches prior behaviour). Other stack viewports stay empty
    // pending drag-drop.
    if (stackVpIndices.includes(0)) {
      const firstSeries = study.series[0];
      if (firstSeries) {
        (async () => {
          if (await detectPdfBeforeLoad(getProvider(), study, firstSeries, acctoken)) {
            markPdfSeries(firstSeries.seriesId);
            setVpSeries(0, firstSeries.seriesId);
            clearVpLoadingPct(0);
            return;
          }
          const imageIds = await buildImageIdsForSeries(study, firstSeries, acctoken!);
          imageIds.forEach((id) => activeIdsRef.current.add(id));
          imageIds.forEach((id) => imageIdToVpRef.current.set(id, 0));

          const viewport: any = renderingEngine.getViewport('viewport-0');
          const element = document.getElementById('viewport-0');
          if (!viewport?.setStack) return;

          seedVpLoadingPct(0, 1);
          setVpSeries(0, firstSeries.seriesId);
          viewport.setStack(imageIds, 0).then(() => {
            applyAutoFitAndWl(viewport, firstSeries.modality || study.modality);
            if (element) {
              try { csToolsUtilities.stackPrefetch.enable(element); } catch {}
            }
            if (study.studyId && getProvider().supports('annotations')) {
              setTimeout(() => {
                loadAnnotations(study.studyId, 0).then((count) => {
                  if (count > 0) {
                    renderingEngineRef.current?.renderViewports(
                      vpInputs.map((v: any) => v.viewportId)
                    );
                  }
                });
              }, 500);
            }
          });
        })();
      }
    }

    const allVpIds = vpInputs.map((v: any) => v.viewportId);
    try {
      renderingEngine.renderViewports(allVpIds);
    } catch (err) {
      console.error('[volume] renderViewports threw:', err);
    }
    // Second render tick after a frame — some drivers need the volume
    // actor to be fully attached before the first successful render,
    // and immediate render can hit a transient shader-compile edge on
    // the first frame after a mode change.
    if (orthoVpIndices.length + threeDVpIndices.length > 0) {
      requestAnimationFrame(() => {
        try {
          const engine = renderingEngineRef.current;
          engine?.renderViewports(allVpIds);
        } catch (err) {
          console.error('[volume] second-tick render threw:', err);
        }
      });
    }
    setVpLoadingPct((prev) => prev.map(() => 0));
    reapplyActiveTool(TOOLGROUP_ID);
  }

  // Handle drag-and-drop
  function handleDrop(e: React.DragEvent, viewportIndex: number) {
    e.preventDefault();
    const seriesIdStr = e.dataTransfer.getData('seriesId');
    if (!seriesIdStr || !acctoken) return;

    const seriesId = Number(seriesIdStr);
    const found = findSeriesAcross(seriesId);
    if (!found) return;
    const { series, study } = found;

    const engine = renderingEngineRef.current;
    if (!engine) return;

    const viewportId = `viewport-${viewportIndex}`;
    const element = document.getElementById(viewportId);
    if (!element) return;

    let viewport = engine.getViewport(viewportId);
    if (!viewport) {
      engine.enableElement({
        viewportId,
        type: csEnums.ViewportType.STACK,
        element,
        defaultOptions: { background: [0, 0, 0] as [number, number, number] },
      });
      const toolGroup = createToolGroup(TOOLGROUP_ID);
      toolGroup?.addViewport(viewportId, RENDERING_ENGINE_ID);
      viewport = engine.getViewport(viewportId);
    }

    (async () => {
      if (await detectPdfBeforeLoad(getProvider(), study, series, acctoken)) {
        markPdfSeries(series.seriesId);
        setVpSeries(viewportIndex, series.seriesId);
        clearVpLoadingPct(viewportIndex);
        return;
      }
      const imageIds = await buildImageIdsForSeries(study, series, acctoken);
      imageIds.forEach((id) => activeIdsRef.current.add(id));

      setVpSeries(viewportIndex, series.seriesId);
      viewport.setStack(imageIds, 0).then(() => {
        applyAutoFitAndWl(viewport, series.modality || study.modality);
        try { csToolsUtilities.stackPrefetch.enable(element); } catch {}
      });
    })();
  }

  /** Load a series into a viewport by index (default: viewport 0). If the
   *  target viewport is currently a volume/3D pane, flip it to Stack first
   *  and reload once the setup effect has rebuilt it. */
  function loadSeries(seriesId: number, viewportIndex = 0) {
    if (!acctoken) return;
    const found = findSeriesAcross(seriesId);
    if (!found) return;

    const currentMode = getViewportMode(viewportModes, viewportIndex);
    if (currentMode !== 'stack') {
      useViewerStore.getState().setViewportMode(viewportIndex, 'stack');
      setTimeout(() => {
        loadSeriesIntoStack(seriesId, viewportIndex);
      }, 100);
      return;
    }

    loadSeriesIntoStack(seriesId, viewportIndex);
  }

  function loadSeriesIntoStack(seriesId: number, viewportIndex: number) {
    if (!acctoken) return;
    // Look up across ALL loaded studies — viewport content is independent
    // of which study is currently active in the side panel. (Items #1, #2)
    const found = findSeriesAcross(seriesId);
    if (!found) return;
    const { series, study } = found;

    const engine = renderingEngineRef.current;
    if (!engine) return;

    const viewportId = `viewport-${viewportIndex}`;
    const element = document.getElementById(viewportId);
    if (!element) return;

    let viewport = engine.getViewport(viewportId);
    let toolGroupRebuilt = false;
    if (!viewport || !viewport.setStack) {
      // Re-create as stack viewport
      destroyToolGroup(TOOLGROUP_ID);
      engine.enableElement({
        viewportId,
        type: csEnums.ViewportType.STACK,
        element,
        defaultOptions: { background: [0, 0, 0] as [number, number, number] },
      });
      const toolGroup = createToolGroup(TOOLGROUP_ID);
      toolGroup?.addViewport(viewportId, RENDERING_ENGINE_ID);
      viewport = engine.getViewport(viewportId);
      toolGroupRebuilt = true;
    }

    // Seed the loading indicator IMMEDIATELY so the user sees feedback
    // before the first XHR even starts. Especially important for HP-driven
    // parallel loads into multiple viewports where each viewport otherwise
    // stays black for several seconds while it waits its turn.
    seedVpLoadingPct(viewportIndex, 1);

    (async () => {
      if (await detectPdfBeforeLoad(getProvider(), study, series, acctoken)) {
        markPdfSeries(series.seriesId);
        setVpSeries(viewportIndex, series.seriesId);
        clearVpLoadingPct(viewportIndex);
        if (toolGroupRebuilt) reapplyActiveTool(TOOLGROUP_ID);
        return;
      }
      const imageIds = await buildImageIdsForSeries(study, series, acctoken);
      imageIds.forEach((id) => activeIdsRef.current.add(id));
      // Route every imageId in this series to viewportIndex so parallel
      // hanging-protocol loads can attribute XHR progress correctly.
      imageIds.forEach((id) => imageIdToVpRef.current.set(id, viewportIndex));

      setVpSeries(viewportIndex, series.seriesId);
      viewport.setStack(imageIds, 0).then(() => {
        applyAutoFitAndWl(viewport, series.modality || study.modality);
        try { csToolsUtilities.stackPrefetch.enable(element); } catch {}
        // The toolGroup rebuild reverts active tool to the createToolGroup
        // default (W/L on Primary). Re-apply the user's selection so loading
        // a series doesn't silently override their picked tool. (#7)
        if (toolGroupRebuilt) reapplyActiveTool(TOOLGROUP_ID);
        // First image is drawn — hide the loading overlay for this vp.
        setVpLoadingPct(prev => { const n = [...prev]; while (n.length <= viewportIndex) n.push(0); n[viewportIndex] = 100; return n; });
      });
    })();
  }

  /** Double-click on viewport image area toggles between fit-to-viewport and
   *  2× zoom centered on the click. Per-viewport state is held on the viewport
   *  itself via a private boolean so each viewport toggles independently. */
  function handleViewportDoubleZoom(viewportIndex: number) {
    const engine = renderingEngineRef.current;
    const vp: any = engine?.getViewport(`viewport-${viewportIndex}`);
    if (!vp) return;
    const zoomed: boolean = vp.__edgewebZoomed === true;
    try {
      if (zoomed) {
        vp.resetCamera?.();
        vp.__edgewebZoomed = false;
      } else {
        // Cornerstone stack: setZoom multiplies parallel scale; for ortho use setCamera
        if (typeof vp.setZoom === 'function') {
          const current = typeof vp.getZoom === 'function' ? vp.getZoom() : 1;
          vp.setZoom(current * 2);
        } else if (vp.getCamera && vp.setCamera) {
          const cam = vp.getCamera();
          vp.setCamera({ ...cam, parallelScale: (cam.parallelScale || 1) / 2 });
        }
        vp.__edgewebZoomed = true;
      }
      vp.render();
    } catch { /* viewport shape without zoom — ignore */ }
  }

  function handleViewportDoubleClick(viewportIndex: number) {
    if (maximizedVp !== null) {
      setMaximizedVp(null);
    } else {
      setMaximizedVp(viewportIndex);
    }
    // Trigger resize so Cornerstone recalculates
    setTimeout(() => {
      renderingEngineRef.current?.resize();
    }, 50);
  }

  function handleSliderChange(viewportIndex: number, newIndex: number) {
    const engine = renderingEngineRef.current;
    if (!engine) return;
    const vp = engine.getViewport(`viewport-${viewportIndex}`);
    if (!vp?.setImageIdIndex) return;
    vp.setImageIdIndex(newIndex);
  }

  /** Parse the currently-displayed imageId in viewport 0 back into a
   *  { seriesId, imageIndex } pair. imageIds we build have the shape
   *    wadouri:<server>/atlrestapi/v2/dicom?seriesId=X&imageIndex=Y&acctoken=...
   *  so regex the query params out. Returns null if we can't resolve. */
  function getCurrentImage(): { seriesId: number; imageIndex: number } | null {
    const engine = renderingEngineRef.current;
    const vp: any = engine?.getViewport('viewport-0');
    if (!vp) return null;
    let imageId: string | undefined;
    try {
      if (vp.getCurrentImageIdIndex && vp.getImageIds) {
        const idx = vp.getCurrentImageIdIndex();
        const ids = vp.getImageIds();
        imageId = ids?.[idx];
      }
    } catch { return null; }
    if (!imageId) return null;
    const m = /seriesId=(\d+)&imageIndex=(\d+)/.exec(imageId);
    if (!m) return null;
    return { seriesId: Number(m[1]), imageIndex: Number(m[2]) };
  }

  /** Jump to a specific image in a series: load the series into viewport 0 if
   *  not already active, then setImageIdIndex on the stack viewport. */
  function jumpToImage(seriesId: number, imageIndex: number) {
    const engine = renderingEngineRef.current;
    if (!engine) return;
    const vp: any = engine.getViewport('viewport-0');
    const current = getCurrentImage();

    if (current && current.seriesId === seriesId && vp?.setImageIdIndex) {
      try { vp.setImageIdIndex(imageIndex); vp.render(); } catch {}
      return;
    }
    // Not loaded — load the series then set the index
    loadSeriesIntoStack(seriesId, 0);
    setTimeout(() => {
      const v: any = renderingEngineRef.current?.getViewport('viewport-0');
      if (v?.setImageIdIndex) {
        try { v.setImageIdIndex(imageIndex); v.render(); } catch {}
      }
    }, 400);
  }

  // Expose imperative handle to parent
  /**
   * Clear any viewports holding series from the closing study.
   *
   * Cornerstone3D has no "empty the stack" call, so we disable + re-enable
   * each affected viewport. That tears the rendered canvas down (no stale
   * pixels) without disturbing other viewports' tool bindings — the
   * toolGroup keeps its membership; we just re-`addViewport` the freshly
   * re-enabled one. State reset (vpSeriesIds[i] = null) collapses the
   * overlay to nothing, completing the visual signal that the study's gone.
   */
  function releaseStudy(closingStudyId: number) {
    const closingStudy = studies.find((s) => s.studyId === closingStudyId);
    if (!closingStudy) return;
    const closingSeriesIds = new Set(closingStudy.series.map((s) => s.seriesId));
    const engine = renderingEngineRef.current;

    vpSeriesIds.forEach((id, idx) => {
      if (id == null || !closingSeriesIds.has(id)) return;
      const viewportId = `viewport-${idx}`;
      try {
        engine?.disableElement(viewportId);
        const element = document.getElementById(viewportId);
        if (element && engine) {
          engine.enableElement({
            viewportId,
            type: csEnums.ViewportType.STACK,
            element,
            defaultOptions: { background: [0, 0, 0] as [number, number, number] },
          });
          const tg = cornerstoneTools.ToolGroupManager.getToolGroup(TOOLGROUP_ID);
          tg?.addViewport(viewportId, RENDERING_ENGINE_ID);
        }
      } catch { /* best-effort — viewport may be in a weird state */ }
    });

    setVpSeriesIds((prev) =>
      prev.map((id) => (id != null && closingSeriesIds.has(id) ? null : id))
    );
  }

  /** Emergency fallback — invoked when Cornerstone's decoder crashes.
   *  Marks every currently-loaded series as a PDF candidate, which flips
   *  the per-viewport render gate to mount PdfViewport. PdfViewport then
   *  tries to extract the PDF from the DICOM file via the provider;
   *  failure there shows a "Couldn't display" message with a Download
   *  button rather than a blank screen. */
  function fallbackAllSeriesToPdf() {
    setPdfSeriesIds((prev) => {
      const next = new Set(prev);
      for (const sid of vpSeriesIds) if (sid != null) next.add(sid);
      // Also consider series from the active study even if no viewport
      // had loaded them yet — that's the typical case for the very first
      // open where the crash beats setup to the punch.
      const study = studies.find((s) => s.studyId === activeStudyId) ?? studies[0];
      study?.series?.forEach((s) => next.add(s.seriesId));
      return next;
    });
    // Make sure something is showing in viewport 0 — covers the case
    // where setupStackViewports never ran.
    const firstStudy = studies.find((s) => s.studyId === activeStudyId) ?? studies[0];
    const firstSeries = firstStudy?.series?.[0];
    if (firstSeries && vpSeriesIds[0] == null) {
      setVpSeries(0, firstSeries.seriesId);
    }
  }

  useImperativeHandle(ref, () => ({
    loadSeries, getCurrentImage, jumpToImage, releaseStudy, fallbackAllSeriesToPdf,
  }));

  return (
    <div
      ref={containerRef}
      className={styles.grid}
      style={maximizedVp !== null ? {
        gridTemplateRows: '1fr',
        gridTemplateColumns: '1fr',
      } : {
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
      }}
    >
      <VolumeWindowControl studyData={studyData} />
      {volumeError && (
        <div className={styles.volumeErrorBanner}>
          <span>⚠ {volumeError}</span>
          <button
            onClick={() => {
              useViewerStore.getState().clearViewportOverrides();
              useViewerStore.getState().setLayout('1x1');
              setVolumeError(null);
            }}
          >
            Switch to Stack
          </button>
          <button className={styles.volumeErrorClose} onClick={() => setVolumeError(null)}>×</button>
        </div>
      )}
      {Array.from({ length: totalViewports }).map((_, i) => {
        // Per-viewport overlay: look up the study/series actually loaded in
        // viewport i (set by loadSeriesIntoStack). vpSeriesIds[i] may be null
        // for empty viewports in a 2x2 / 3x3 layout — overlay just hides then.
        const vpSid = vpSeriesIds[i] ?? null;
        const found = vpSid != null ? findSeriesAcross(vpSid) : null;
        const series = found?.series;
        const vpStudy = found?.study;
        const isHidden = maximizedVp !== null && maximizedVp !== i;
        const loadPct = vpLoadingPct[i] ?? 0;
        // Render gate: trust either the modality tag (cheap) OR the
        // peek-and-parse confirmation that ran in the load path. Without
        // the peek result, series tagged with imaging modality codes
        // that turn out to actually be PDFs would silently break.
        const showPdf = !!(
          series && vpStudy &&
          (isPdfSeries(series) || (vpSid != null && pdfSeriesIds.has(vpSid)))
        );

        return (
          <div
            key={`${layout}-${i}`}
            id={`viewport-${i}`}
            className={[
              styles.viewport,
              isHidden ? styles.vpHidden : '',
              // Active-viewport highlight applies on ANY layout (including 1x1)
              // so the user can always see which viewport their tool actions
              // are targeting. Previously this was gated to multi-viewport
              // layouts but with the study-switch layout-reset to 1x1 that
              // meant the indicator never showed on a freshly-opened study.
              i === activeViewportIndex ? styles.vpActive : '',
            ].filter(Boolean).join(' ')}
            onMouseDown={() => onActiveViewportChange?.(i)}
            onDoubleClick={(e) => {
              // Ignore dblclicks on the maximize button and other controls
              if ((e.target as HTMLElement).closest('button')) return;
              handleViewportDoubleZoom(i);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, i)}
          >
            {/* Per-viewport mode picker (top-left). Skip on PDF viewports
                — they don't render via Cornerstone so mode changes make
                no sense. Pass modality so MPR/MIP/3D are hidden for 2D-
                only modalities (MG, CR, DX, US, ...). Falls back to the
                study modality when the viewport has no series assigned. */}
            {!showPdf && (
              <ModePicker
                viewportIndex={i}
                modality={series?.modality || vpStudy?.modality || studyData?.modality}
              />
            )}

            {/* Maximize/Restore button */}
            {totalViewports > 1 && (
              <button
                className={styles.maximizeBtn}
                onClick={(e) => { e.stopPropagation(); handleViewportDoubleClick(i); }}
                title={maximizedVp !== null ? 'Restore layout' : 'Maximize viewport'}
              >
                {maximizedVp !== null ? '⊡' : '⊞'}
              </button>
            )}

            {/* DICOM Encapsulated PDF overlay — kicks in when the series'
                modality is DOC. Sits above the empty Cornerstone canvas. */}
            {showPdf && vpStudy && series && (
              <PdfViewport
                label={`${vpStudy.patientName} · ${series.seriesDescription || series.modality}`}
                cacheKey={`${vpStudy.studyId}/${series.seriesId}`}
                load={() => getProvider().getPdfBlobUrl(vpStudy, series, acctoken || undefined)}
              />
            )}

            {/* DICOM text overlays — patient info comes from THIS viewport's
                study, not the active one, so a 2x2 mixing studies A and B
                shows the right patient banner over each pane. */}
            {!showPdf && overlaysVisible && series && (
              <div className={styles.overlay}>
                <div className={styles.overlayTL}>
                  {vpStudy?.patientName}
                  <br />
                  {vpStudy?.patientId}
                  {vpStudy?.patientSex && <> &middot; {vpStudy.patientSex}</>}
                  {vpStudy?.patientAge && <> &middot; {vpStudy.patientAge}</>}
                </div>
                <div className={styles.overlayTR}>
                  {series.seriesDescription}
                  <br />
                  {series.modality}
                  {series.bodyPart && <> &middot; {series.bodyPart}</>}
                </div>
                <div className={styles.overlayBL}>
                  {vpStudy?.studyDate}
                  {vpMeta[i] && vpMeta[i].ww > 0 && (
                    <>
                      <br />
                      WW: {vpMeta[i].ww} / WC: {vpMeta[i].wc}
                    </>
                  )}
                  {vpMeta[i] && vpMeta[i].zoom > 0 && (
                    <>
                      <br />
                      Zoom: {vpMeta[i].zoom}x
                    </>
                  )}
                </div>
                <div className={styles.overlayBR}>
                  {sliceInfo[i] && sliceInfo[i].total > 1
                    ? `${sliceInfo[i].current + 1} / ${sliceInfo[i].total}`
                    : `${series.numImages} img`}
                </div>
              </div>
            )}

            {/* Per-image XHR loading overlay (for large CR/MG images).
                Suppressed for PDF series — they don't go through Cornerstone's
                image pipeline, so loadPct never advances past its initial 1%
                and would otherwise leave the viewport dimmed behind the
                PdfViewport iframe. */}
            {series && !showPdf && loadPct > 0 && loadPct < 100 && (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingText}>{loadPct}%</div>
                <div className={styles.loadingLabel}>Loading…</div>
                <div className={styles.loadingBar}>
                  <div
                    className={styles.loadingBarFill}
                    style={{ width: `${loadPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Volume-build overlay — shows while a volumetric viewport is
                waiting for geometry fetch + createAndCacheVolume + setVolumes
                to complete. Prevents the "dead viewport" perception during
                the potentially-multi-second volume construction. Uses an
                indeterminate progress bar (no percentage available). */}
            {!showPdf && vpBuildingVolume[i] && (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingLabel}>Building volume…</div>
                <div className={styles.loadingBar}>
                  <div className={styles.loadingBarFillIndeterminate} />
                </div>
              </div>
            )}

            {/* Slice slider — only for stack viewports with >1 image */}
            {getViewportMode(viewportModes, i) === 'stack' && sliceInfo[i] && sliceInfo[i].total > 1 && (
              <div
                className={styles.sliceSlider}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerMove={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <input
                  type="range"
                  min={0}
                  max={sliceInfo[i].total - 1}
                  value={sliceInfo[i].current}
                  onChange={(e) => handleSliderChange(i, Number(e.target.value))}
                  className={styles.sliceRange}
                  title={`Slice ${sliceInfo[i].current + 1} / ${sliceInfo[i].total}`}
                />
                <span className={styles.sliceLabel}>
                  {sliceInfo[i].current + 1}/{sliceInfo[i].total}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

export default ViewportGrid;
